/**
 * VinylOrderModesPanel — reusable admin card for editing the three
 * vinyl order-mode toggles (Quick / Guided / Expert) plus the default
 * mode selector.
 *
 * Two modes:
 *   * Global (default) — GETs / PUTs `/api/admin/system-settings/vinyl-order-modes`.
 *   * Company override — pass `organizationId`. GETs / PUTs
 *     `/api/admin/organizations/{org_id}/vinyl-order-modes` and adds a
 *     "Use global settings" toggle at the top; when unchecked, admins
 *     edit an override for that company only.
 *
 * The panel enforces the same "default must be enabled" invariant the
 * backend sanitizer applies — the disabled-mode radio option is greyed
 * out, and picking a mode auto-flips it back on.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Zap, Palette, Settings2, Save } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { Label } from '../ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

const MODE_META = {
  quick:  { label: 'Quick Order',   icon: Zap,       tint: 'text-amber-600'  },
  visual: { label: 'Guided Setup',  icon: Palette,   tint: 'text-purple-600' },
  expert: { label: 'Expert Setup',  icon: Settings2, tint: 'text-slate-600'  },
};
const MODE_KEYS = ['quick', 'visual', 'expert'];

// Client-side sanitizer — mirrors backend `_sanitize_vinyl_modes`.
// Keeps the "default must be enabled + at least one mode enabled"
// invariant true across every keystroke so the UI never shows an
// impossible state.
function sanitize(cfg) {
  const modes = {};
  for (const k of MODE_KEYS) modes[k] = { enabled: cfg?.modes?.[k]?.enabled !== false };
  if (!MODE_KEYS.some((k) => modes[k].enabled)) modes.quick.enabled = true;
  let def = cfg?.default_mode || 'quick';
  if (!MODE_KEYS.includes(def) || !modes[def].enabled) {
    def = MODE_KEYS.find((k) => modes[k].enabled);
  }
  return { modes, default_mode: def };
}

export default function VinylOrderModesPanel({ organizationId = null }) {
  const isCompany = Boolean(organizationId);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Global mode: just the config. Company mode: also `useGlobal` toggle
  // + reference to the resolved global values (shown as read-only when
  // "Use global settings" is on).
  const [config, setConfig] = useState(null);
  const [useGlobal, setUseGlobal] = useState(true);
  const [globalRef, setGlobalRef] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = isCompany
        ? `${API}/api/admin/organizations/${organizationId}/vinyl-order-modes`
        : `${API}/api/admin/system-settings/vinyl-order-modes`;
      const { data } = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      if (isCompany) {
        setGlobalRef(sanitize(data.global));
        if (data.override) {
          setUseGlobal(false);
          setConfig(sanitize(data.override));
        } else {
          setUseGlobal(true);
          setConfig(sanitize(data.effective));
        }
      } else {
        setConfig(sanitize(data));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load order modes');
    } finally {
      setLoading(false);
    }
  }, [isCompany, organizationId]);

  useEffect(() => { load(); }, [load]);

  const toggleMode = (k, next) => {
    setConfig((prev) => sanitize({ ...prev, modes: { ...prev.modes, [k]: { enabled: next } } }));
  };

  const pickDefault = (k) => {
    // Auto-enable the mode when picking it as default — friendlier than
    // rejecting the click, matches the backend safety net.
    setConfig((prev) => sanitize({
      ...prev,
      modes: { ...prev.modes, [k]: { enabled: true } },
      default_mode: k,
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (isCompany) {
        const body = { override: useGlobal ? null : config };
        await axios.put(
          `${API}/api/admin/organizations/${organizationId}/vinyl-order-modes`,
          body,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.put(
          `${API}/api/admin/system-settings/vinyl-order-modes`,
          config,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      toast.success('Order-mode settings saved');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return (
      <Card><CardContent className="py-10 flex items-center justify-center text-slate-500">
        <Loader2 className="h-5 w-5 mr-2 animate-spin" /> Loading…
      </CardContent></Card>
    );
  }

  const readOnly = isCompany && useGlobal;
  const shown = readOnly ? globalRef : config;

  return (
    <Card data-testid="vinyl-order-modes-panel">
      <CardHeader>
        <CardTitle className="text-base">Vinyl Order Modes</CardTitle>
        <CardDescription>
          Enable/disable the three modes customers see when configuring a vinyl order,
          and pick which one is highlighted as the default. Disabled modes stay visible
          to customers but are greyed out and cannot be selected.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isCompany && (
          <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div>
              <Label htmlFor="use-global" className="text-sm font-medium">Use global settings</Label>
              <p className="text-xs text-slate-500 mt-0.5">
                Off = override just this company. On = follow the platform-wide setting.
              </p>
            </div>
            <Switch
              id="use-global"
              checked={useGlobal}
              onCheckedChange={(v) => {
                setUseGlobal(v);
                if (v && globalRef) setConfig(globalRef);
              }}
              data-testid="vinyl-modes-use-global"
            />
          </div>
        )}

        <div className="space-y-3">
          {MODE_KEYS.map((k) => {
            const meta = MODE_META[k];
            const Icon = meta.icon;
            const enabled = shown.modes[k].enabled;
            const isDefault = shown.default_mode === k;
            return (
              <div key={k} className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 p-3">
                <div className="flex items-center gap-3">
                  <Icon className={`h-5 w-5 ${meta.tint}`} />
                  <div>
                    <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
                      {meta.label}
                      {isDefault && (
                        <span className="inline-block rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-700 uppercase tracking-wide" data-testid={`vinyl-modes-${k}-default-badge`}>
                          Default
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">
                      {enabled ? 'Enabled — customers can pick this mode' : 'Disabled — shown greyed out to customers'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {!isDefault && enabled && (
                    <button
                      type="button"
                      onClick={() => !readOnly && pickDefault(k)}
                      disabled={readOnly}
                      className="text-xs text-slate-600 hover:text-slate-900 disabled:opacity-50"
                      data-testid={`vinyl-modes-${k}-set-default`}
                    >
                      Set as default
                    </button>
                  )}
                  <Switch
                    checked={enabled}
                    disabled={readOnly}
                    onCheckedChange={(v) => toggleMode(k, v)}
                    data-testid={`vinyl-modes-${k}-toggle`}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {!readOnly && (
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="vinyl-modes-save">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
